import React, { useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { Button, Icon, Checkbox, Select, H1, H3, useGraphQLMutation, useGraphQLQuery } from '@tviklax/ui-components';
import { useRole } from '../../../context/RoleContext';
import { useCurrencyFormatter } from '../../../hooks/useCurrencyFormatter';
import {
  ReconciliationContainer,
  PageHeader,
  PageHeaderLeft,
  PageHeaderRight,
  ActionButtonWrapper,
  ActionMenu,
  PageDescription,
  Subtitle,
  Tooltip,
  FilterBar,
  FilterLabel,
  FilterCount,
  SnapshotGrid,
  SnapshotCard,
  SnapshotTitle,
  SnapshotValue,
  MetricsBar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableHeader,
  AgentGrid,
  AgentCard,
  AgentName,
  AgentMetric,
  ActionLinks,
  ActionLink,
  RestrictedView,
} from './ReconciliationOverview.styles';

interface ReconciliationOverviewProps {
  highlightOrderId?: string;
  onOpenOrder?: (orderId: string) => void;
  onOpenComplianceOrder?: (orderId: string) => void;
  onOpenShipment?: (shipmentId: string) => void;
}

type AgingBucket = 'All' | '0-15' | '16-30' | '31-60' | '60+';
type ShipmentStatusFilter = 'All' | 'Blocked' | 'Pending' | 'Approved';

const RECONCILIATION_QUERY = gql`
  query ReconciliationData($limit: Int = 500, $offset: Int = 0) {
    orders(limit: $limit, offset: $offset) {
      id
      orderNo
      finalAmount
      orderStatus
      currency
    }
    orderPayments(limit: $limit, offset: $offset) {
      id
      orderId
      amount
      paymentStatus
      paymentMethod
      createdAt
      currency
    }
    forexComplianceRecords(limit: $limit, offset: $offset) {
      id
      buyer
      supplier
      country
      currency
      totalValue
      complianceStatus
      isLocked
      remittancePlan {
        tranches {
          percentage
          amount
          date
          milestone
        }
      }
      approvalHistory {
        timestamp
        action
        by
        reason
        newStatus
      }
    }
  }
`;

const BLOCK_ORDER_MUTATION = gql`
  mutation BlockOrder($input: BlockOrderInput!) {
    blockOrder(input: $input) {
      success
      message
      orderId
    }
  }
`;

const UNBLOCK_ORDER_MUTATION = gql`
  mutation UnblockOrder($input: UnblockOrderInput!) {
    unblockOrder(input: $input) {
      success
      message
      orderId
    }
  }
`;

const SEND_PAYMENT_REMINDER_MUTATION = gql`
  mutation SendPaymentReminder($input: SendPaymentReminderInput!) {
    sendPaymentReminder(input: $input) {
      success
      message
      orderId
    }
  }
`;

const ASSIGN_TO_COLLECTIONS_MUTATION = gql`
  mutation AssignToCollections($input: AssignToCollectionsInput!) {
    assignToCollections(input: $input) {
      success
      message
      orderId
    }
  }
`;

interface ReconciliationOrderQueryItem {
  id: number;
  orderNo?: string | null;
  finalAmount?: number | null;
  orderStatus?: number | null;
  currency?: string | null;
}

interface ReconciliationPaymentQueryItem {
  id: number;
  orderId: number;
  amount: number;
  paymentStatus?: number | null;
  paymentMethod?: string | null;
  createdAt?: string | null;
  currency?: string | null;
}

interface ReconciliationForexTrancheQueryItem {
  percentage?: number | null;
  amount?: number | null;
  date?: string | null;
  milestone?: string | null;
}

interface ReconciliationForexApprovalHistoryQueryItem {
  timestamp?: string | null;
  action?: string | null;
  by?: string | null;
  reason?: string | null;
  newStatus?: string | null;
}

interface ReconciliationForexRecordQueryItem {
  id: string;
  buyer: string;
  supplier: string;
  country: string;
  currency: string;
  totalValue: number;
  complianceStatus: string;
  isLocked: boolean;
  remittancePlan?: {
    tranches?: ReconciliationForexTrancheQueryItem[];
  };
  approvalHistory?: ReconciliationForexApprovalHistoryQueryItem[];
}

interface ReconciliationQueryData {
  orders: ReconciliationOrderQueryItem[];
  orderPayments: ReconciliationPaymentQueryItem[];
  forexComplianceRecords: ReconciliationForexRecordQueryItem[];
}

interface ReconciliationRow {
  customer: string;
  orders: string[];
  outstanding: number;
  bucket: AgingBucket;
  supplier: string;
  shipmentStatus: 'Blocked' | 'Pending' | 'Approved';
  lastAction: string;
  nextAction: string;
}

interface SupplierRiskRow {
  supplier: string;
  outstandingLinked: number;
  overduePct: number;
  blockedCount: number;
  escalations: number;
}

const resolveOrderLabel = (orderNo: string | null | undefined, orderId: number): string => {
  const normalized = String(orderNo || '').trim();
  return normalized || `ORD-${orderId}`;
};

const resolveRecordOrderLabel = (recordId: string, ordersByOrderNo: Map<string, ReconciliationOrderQueryItem>): string => {
  const normalized = String(recordId || '').trim();
  if (!normalized) return '';
  if (ordersByOrderNo.has(normalized)) return normalized;

  const suffixStripped = normalized.replace(/-\d+$/, '');
  if (ordersByOrderNo.has(suffixStripped)) return suffixStripped;

  return normalized;
};

const getStatusLabel = (complianceStatus: string, isLocked: boolean): 'Blocked' | 'Pending' | 'Approved' => {
  const normalized = complianceStatus.toLowerCase();
  if (normalized === 'remittance-completed') return 'Approved';
  if (isLocked || normalized === 'locked' || normalized === 'remittance-initiated') return 'Blocked';
  if (normalized === 'compliance-approved' || normalized === 'remittance-completed') return 'Approved';
  return 'Pending';
};

const getBucketFromStatus = (status: string, outstanding: number): AgingBucket => {
  const normalized = status.toLowerCase();
  if (outstanding <= 0) return '0-15';
  if (normalized === 'locked') return '60+';
  if (normalized === 'remittance-initiated') return '31-60';
  if (normalized === 'submitted' || normalized === 'docs-requested') return '16-30';
  return '0-15';
};

const getNextAction = (shipmentStatus: 'Blocked' | 'Pending' | 'Approved', complianceStatus: string): string => {
  if (shipmentStatus === 'Blocked') return 'Maintain block';
  if (shipmentStatus === 'Pending') return 'Review compliance';
  if (complianceStatus.toLowerCase() === 'remittance-completed') return 'No action (completed)';
  if (complianceStatus.toLowerCase() === 'compliance-approved') return 'Monitor remittance';
  return 'Trigger reminder';
};

const getLastAction = (record: ReconciliationForexRecordQueryItem): string => {
  const latest = (record.approvalHistory || [])[0];
  if (latest?.action) {
    return latest.reason ? `${latest.action} · ${latest.reason}` : latest.action;
  }
  return record.isLocked ? 'Locked for review' : record.complianceStatus.replace(/-/g, ' ');
};

const deriveOutstanding = (recordValue: number, paidAmount: number): number => Math.max(recordValue - paidAmount, 0);

const formatPercentage = (value: number, total: number): number => {
  if (total <= 0) return 0;
  return Math.round((value / total) * 100);
};

export const ReconciliationOverview: React.FC<ReconciliationOverviewProps> = (props) => {
  const { highlightOrderId, onOpenOrder, onOpenComplianceOrder, onOpenShipment } = props;
  const { can, role } = useRole();
  const { formatCurrency } = useCurrencyFormatter();
  const canView = can('recon.view');
  const canManage = can('recon.manage');
  const isAgent = role === 'agent';

  const { data: reconciliationData } = useGraphQLQuery<ReconciliationQueryData>(RECONCILIATION_QUERY, {
    variables: { limit: 500, offset: 0 },
    showErrorToast: true,
    fetchPolicy: 'network-only',
  });
  const [blockOrder] = useGraphQLMutation(BLOCK_ORDER_MUTATION, {
    showErrorToast: true,
    showSuccessToast: false,
  });
  const [unblockOrder] = useGraphQLMutation(UNBLOCK_ORDER_MUTATION, {
    showErrorToast: true,
    showSuccessToast: false,
  });
  const [sendPaymentReminder] = useGraphQLMutation(SEND_PAYMENT_REMINDER_MUTATION, {
    showErrorToast: true,
    showSuccessToast: false,
  });
  const [assignToCollections] = useGraphQLMutation(ASSIGN_TO_COLLECTIONS_MUTATION, {
    showErrorToast: true,
    showSuccessToast: false,
  });

  // Role-based access:
  // - Admin, Finance, Ops, Sales: Full dashboard (Finance gets write actions)
  // - Agent: Should see their own mini-view (not this control tower)
  // - Compliance, Customer, Supplier: Hidden
  
  // NOTE: Agents should be redirected to their own "Linked Orders & Outstanding" view
  // This control tower is for internal finance operations only
  if (isAgent) {
    return (
      <ReconciliationContainer>
        <PageHeader>
          <PageHeaderLeft>
            <Icon name="AlertCircle" size={20} />
            <H1 style={{ margin: '0' }}>Reconciliation & Risk</H1>
          </PageHeaderLeft>
        </PageHeader>
        <RestrictedView>
          <Icon name="AlertCircle" size={18} />
          Agents have access to their own linked orders view. This control tower is for internal finance operations.
        </RestrictedView>
      </ReconciliationContainer>
    );
  }

  // Filter state
  const [selectedAgent, setSelectedAgent] = useState<string>('All');
  const [selectedBucket, setSelectedBucket] = useState<AgingBucket>('All');
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [selectedShipmentStatus, setSelectedShipmentStatus] = useState<ShipmentStatusFilter>('All');
  
  // Action menu state
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleRowAction = async (action: 'block' | 'unblock' | 'remind' | 'escalate', orderId: string) => {
    try {
      if (action === 'block') {
        await blockOrder({ variables: { input: { orderId, reason: 'Blocked from reconciliation workflow', actor: 'Finance UI' } } });
      }
      if (action === 'unblock') {
        await unblockOrder({ variables: { input: { orderId, reason: 'Unblocked from reconciliation workflow', actor: 'Finance UI' } } });
      }
      if (action === 'remind') {
        await sendPaymentReminder({
          variables: {
            input: {
              orderId,
              recipient: 'customer',
              message: `Payment reminder triggered from reconciliation for ${orderId}`,
              actor: 'Finance UI',
            },
          },
        });
      }
      if (action === 'escalate') {
        await assignToCollections({
          variables: {
            input: {
              orderId,
              reason: `Escalated from reconciliation workflow for ${orderId}`,
              actor: 'Finance UI',
            },
          },
        });
      }
    } catch {
      // Error toast is handled by useGraphQLMutation.
    }
  };
  
  // Close action menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setShowActionMenu(false);
    if (showActionMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showActionMenu]);
  
  const handleBulkAction = (action: string) => {
    const targetOrderId = rowsWithHighlight[0]?.orders[0];
    if (!targetOrderId) {
      setShowActionMenu(false);
      return;
    }

    setShowActionMenu(false);
    if (onOpenComplianceOrder) {
      onOpenComplianceOrder(targetOrderId);
      return;
    }

    if (onOpenOrder) {
      onOpenOrder(targetOrderId);
      return;
    }

    console.log(`Bulk action ${action} for ${targetOrderId}`);
  };
  
  // Navigation handlers - Click-through rules:
  // 1. Order ID → Order detail page → Payments tab
  // 2. Shipment ID → Shipment detail page → Block reason visible
  // 3. Agent card → Agent profile page (future)
  // This page is command + navigate, not edit.
  
  const handleShipmentClick = (orderId: string) => {
    if (onOpenOrder) {
      onOpenOrder(orderId);
    } else if (onOpenShipment) {
      onOpenShipment(orderId);
    } else {
      console.log(`Navigate to order: ${orderId}`);
      // In production: navigate to /payments with order selected and focus on block reason
    }
  };
  
  const handleAgentClick = (agentName: string) => {
    console.log(`Navigate to agent profile: ${agentName}`);
    // In production: navigate to /agents/{agentId}
  };

  // Get unique agents for filter
  const liveRows = useMemo(() => {
    const orders = reconciliationData?.orders || [];
    const payments = reconciliationData?.orderPayments || [];
    const forexRecords = reconciliationData?.forexComplianceRecords || [];

    const ordersById = new Map<number, ReconciliationOrderQueryItem>();
    const ordersByOrderNo = new Map<string, ReconciliationOrderQueryItem>();
    orders.forEach((order) => {
      ordersById.set(order.id, order);
      if (order.orderNo) {
        ordersByOrderNo.set(order.orderNo, order);
      }
    });

    const paidTotals = new Map<number, number>();
    payments.forEach((payment) => {
      paidTotals.set(payment.orderId, (paidTotals.get(payment.orderId) || 0) + Number(payment.amount || 0));
    });

    const grouped = new Map<string, ReconciliationRow & { blockedReasons: Set<string>; supplierNames: Set<string>; recordIds: Set<string>; totalOutstanding: number; totalValue: number; paidValue: number }>();

    forexRecords.forEach((record) => {
      const resolvedOrderLabel = resolveRecordOrderLabel(record.id, ordersByOrderNo);
      const matchedOrder = ordersByOrderNo.get(resolvedOrderLabel) || ordersById.get(Number(resolvedOrderLabel.replace(/[^0-9]/g, '')) || 0);
      const orderLabel = matchedOrder ? resolveOrderLabel(matchedOrder.orderNo, matchedOrder.id) : resolvedOrderLabel;
      const orderValue = Number(matchedOrder?.finalAmount || record.totalValue || 0);
      const paidValue = matchedOrder ? (paidTotals.get(matchedOrder.id) || 0) : 0;
      const outstanding = deriveOutstanding(orderValue, paidValue);
      const shipmentStatus = getStatusLabel(record.complianceStatus, record.isLocked);
      const bucket = getBucketFromStatus(record.complianceStatus, outstanding);
      const customer = record.buyer || 'Unknown Buyer';
      const supplier = record.supplier || 'Unassigned Supplier';
      const blockedReason = record.isLocked
        ? 'Compliance locked'
        : record.complianceStatus.replace(/-/g, ' ');
      const nextAction = getNextAction(shipmentStatus, record.complianceStatus);
      const lastAction = getLastAction(record);

      const existing = grouped.get(customer);
      if (!existing) {
        grouped.set(customer, {
          customer,
          orders: [orderLabel],
          outstanding,
          bucket,
          supplier,
          shipmentStatus,
          lastAction,
          nextAction,
          blockedReasons: new Set([blockedReason]),
          supplierNames: new Set([supplier]),
          recordIds: new Set([record.id]),
          totalOutstanding: outstanding,
          totalValue: orderValue,
          paidValue,
        });
        return;
      }

      existing.orders.push(orderLabel);
      existing.outstanding += outstanding;
      existing.totalOutstanding += outstanding;
      existing.totalValue += orderValue;
      existing.paidValue += paidValue;
      existing.bucket = outstanding > existing.outstanding ? bucket : existing.bucket;
      existing.shipmentStatus = shipmentStatus === 'Blocked' ? 'Blocked' : existing.shipmentStatus === 'Blocked' ? 'Blocked' : (shipmentStatus === 'Pending' ? 'Pending' : existing.shipmentStatus);
      existing.lastAction = existing.lastAction || lastAction;
      existing.nextAction = existing.nextAction || nextAction;
      existing.blockedReasons.add(blockedReason);
      existing.supplierNames.add(supplier);
      existing.recordIds.add(record.id);
    });

    const rows = Array.from(grouped.values()).map((row) => ({
      customer: row.customer,
      orders: Array.from(new Set(row.orders)),
      outstanding: row.outstanding,
      bucket: row.bucket,
      supplier: Array.from(row.supplierNames).join(', '),
      shipmentStatus: row.shipmentStatus,
      lastAction: row.lastAction,
      nextAction: row.nextAction,
      recordIds: Array.from(row.recordIds),
      blockedReasons: Array.from(row.blockedReasons),
      totalOutstanding: row.totalOutstanding,
      totalValue: row.totalValue,
      paidValue: row.paidValue,
    }));

    return rows;
  }, [reconciliationData?.forexComplianceRecords, reconciliationData?.orderPayments, reconciliationData?.orders]);

  const agingSnapshot = useMemo(() => {
    const totalOutstanding = liveRows.reduce((sum, row) => sum + row.outstanding, 0);
    const overdue0_15 = liveRows.filter((row) => row.bucket === '0-15').reduce((sum, row) => sum + row.outstanding, 0);
    const overdue16_30 = liveRows.filter((row) => row.bucket === '16-30').reduce((sum, row) => sum + row.outstanding, 0);
    const overdue31_60 = liveRows.filter((row) => row.bucket === '31-60').reduce((sum, row) => sum + row.outstanding, 0);
    const critical60Plus = liveRows.filter((row) => row.bucket === '60+').reduce((sum, row) => sum + row.outstanding, 0);
    const blocked = liveRows.filter((row) => row.shipmentStatus === 'Blocked').length;
    const pending = liveRows.filter((row) => row.shipmentStatus === 'Pending').length;
    const approved = liveRows.filter((row) => row.shipmentStatus === 'Approved').length;
    const total = Math.max(liveRows.length, 1);

    return {
      totalOutstanding,
      overdue0_15,
      overdue16_30,
      overdue31_60,
      critical60Plus,
      blockedPct: formatPercentage(blocked, total),
      agentLinkedPct: formatPercentage(pending, total),
      cleanPendingPct: formatPercentage(approved, total),
    };
  }, [liveRows]);

  const agentsList = useMemo(() => {
    const unique = Array.from(new Set(liveRows.flatMap((r) => r.supplier.split(', ').map((item) => item.trim()).filter(Boolean))));
    return ['All', ...unique];
  }, [liveRows]);

  // Apply filters
  const filteredRows = useMemo(() => {
    let filtered = liveRows;

    if (selectedAgent !== 'All') {
      filtered = filtered.filter(r => r.supplier.split(', ').map((item) => item.trim()).includes(selectedAgent));
    }

    if (selectedBucket !== 'All') {
      filtered = filtered.filter(r => r.bucket === selectedBucket);
    }

    if (blockedOnly) {
      filtered = filtered.filter(r => r.shipmentStatus === 'Blocked');
    } else if (selectedShipmentStatus !== 'All') {
      filtered = filtered.filter(r => r.shipmentStatus === selectedShipmentStatus);
    }

    return filtered;
  }, [liveRows, selectedAgent, selectedBucket, blockedOnly, selectedShipmentStatus]);

  const rowsWithHighlight = useMemo(() => {
    if (!highlightOrderId) return filteredRows;
    const match = filteredRows.find(r => r.orders.includes(highlightOrderId));
    if (!match) return filteredRows;
    const others = filteredRows.filter(r => !r.orders.includes(highlightOrderId));
    return [match, ...others];
  }, [highlightOrderId, filteredRows]);

  const blockedOrders = useMemo(() => {
    return rowsWithHighlight
      .filter((row) => row.shipmentStatus === 'Blocked')
      .flatMap((row) =>
        row.orders.map((orderId) => ({
          orderId,
          customer: row.customer,
          supplier: row.supplier,
          reason: row.lastAction || 'Compliance locked',
          unlockCondition: row.nextAction,
        }))
      );
  }, [rowsWithHighlight]);

  const supplierRiskSummary = useMemo<SupplierRiskRow[]>(() => {
    const bySupplier = new Map<string, { outstandingLinked: number; overdueCount: number; totalCount: number; blockedCount: number; escalations: number }>();

    liveRows.forEach((row) => {
      row.supplier.split(', ').map((item) => item.trim()).filter(Boolean).forEach((supplier) => {
        const current = bySupplier.get(supplier) || { outstandingLinked: 0, overdueCount: 0, totalCount: 0, blockedCount: 0, escalations: 0 };
        current.outstandingLinked += row.outstanding;
        current.totalCount += 1;
        current.overdueCount += row.bucket === '60+' ? 1 : 0;
        current.blockedCount += row.shipmentStatus === 'Blocked' ? 1 : 0;
        current.escalations += row.shipmentStatus === 'Blocked' ? 1 : 0;
        bySupplier.set(supplier, current);
      });
    });

    return Array.from(bySupplier.entries()).map(([supplier, summary]) => ({
      supplier,
      outstandingLinked: summary.outstandingLinked,
      overduePct: formatPercentage(summary.overdueCount, summary.totalCount || 1),
      blockedCount: summary.blockedCount,
      escalations: summary.escalations,
    }));
  }, [liveRows]);

  if (!canView) {
    return (
      <ReconciliationContainer>
        <PageHeader>
          <Icon name="AlertCircle" size={20} />
          <H1 style={{ margin: '0' }}>Reconciliation & Risk</H1>
        </PageHeader>
        <RestrictedView>
          <Icon name="Lock" size={18} />
          This page is restricted. Only Finance (and Admin/Ops read-only) can view.
        </RestrictedView>
      </ReconciliationContainer>
    );
  }

  return (
    <ReconciliationContainer>
      <PageHeader>
        <PageHeaderLeft>
          <div>
            <Icon name="AlertCircle" size={20} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '8px' }} />
            <H1 style={{ display: 'inline-block', verticalAlign: 'middle', margin: '0' }}>Finance / Reconciliation & Risk</H1>
            <PageDescription>
              Monitor financial exposure, payment aging, and shipments blocked due to finance risk.
            </PageDescription>
          </div>
        </PageHeaderLeft>
        {canManage && (
          <PageHeaderRight>
            <ActionButtonWrapper>
              <Button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowActionMenu(!showActionMenu);
                }}
              >
                <Icon name="Zap" size={16} />
                Take Action
              </Button>
              {showActionMenu && (
                <ActionMenu>
                  <Button variant="ghost" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => handleBulkAction('export-aging-report')}>
                    📊 Export Aging Report
                  </Button>
                  <Button variant="ghost" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => handleBulkAction('send-reminders')}>
                    📧 Send Reminders
                  </Button>
                  <Button variant="ghost" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => handleBulkAction('escalate-internally')}>
                    ⚠️ Escalate Internally
                  </Button>
                  <Button variant="ghost" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => handleBulkAction('maintain-block')}>
                    🔒 Maintain Block
                  </Button>
                  <Button variant="ghost" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => handleBulkAction('approve-release')}>
                    ✅ Approve Temporary Release (Override)
                  </Button>
                </ActionMenu>
              )}
            </ActionButtonWrapper>
          </PageHeaderRight>
        )}
      </PageHeader>

      {/* Sticky Filter Bar */}
      <FilterBar>
        <FilterLabel>Filters:</FilterLabel>
        
        <Select
          value={selectedAgent}
          onChange={(e) => setSelectedAgent(e.target.value)}
          items={agentsList.map(a => ({ label: a === 'All' ? 'All Agents' : a, value: a }))}
          size="sm"
        />
        
        <Select
          value={selectedBucket}
          onChange={(e) => setSelectedBucket(e.target.value as AgingBucket)}
          items={[
            { label: 'All Aging', value: 'All' },
            { label: '0-15 days', value: '0-15' },
            { label: '16-30 days', value: '16-30' },
            { label: '31-60 days', value: '31-60' },
            { label: '60+ days', value: '60+' }
          ]}
          size="sm"
        />
        
        <Select
          value={selectedShipmentStatus}
          onChange={(e) => setSelectedShipmentStatus(e.target.value as ShipmentStatusFilter)}
          items={[
            { label: 'All Statuses', value: 'All' },
            { label: 'Blocked', value: 'Blocked' },
            { label: 'Pending', value: 'Pending' },
            { label: 'Approved', value: 'Approved' }
          ]}
          size="sm"
        />
        
        <Checkbox
          label="Blocked only"
          checked={blockedOnly}
          onChange={(e) => setBlockedOnly(e.target.checked)}
        />
        
        <FilterCount>
          Showing {rowsWithHighlight.length} of {liveRows.length} rows
        </FilterCount>
      </FilterBar>

      {/* Section 1: Aging Snapshot */}
      <SnapshotGrid>
        <SnapshotCard $bg="#ecfdf5">
          <SnapshotTitle>Total Outstanding</SnapshotTitle>
          <SnapshotValue $color="#0f766e">{formatCurrency(agingSnapshot.totalOutstanding, 'INR')}</SnapshotValue>
        </SnapshotCard>
        <SnapshotCard $bg="#fffbeb">
          <SnapshotTitle>Overdue (0–15)</SnapshotTitle>
          <SnapshotValue $color="#b45309">{formatCurrency(agingSnapshot.overdue0_15, 'INR')}</SnapshotValue>
        </SnapshotCard>
        <SnapshotCard $bg="#fffbeb">
          <SnapshotTitle>Overdue (16–30)</SnapshotTitle>
          <SnapshotValue $color="#b45309">{formatCurrency(agingSnapshot.overdue16_30, 'INR')}</SnapshotValue>
        </SnapshotCard>
        <SnapshotCard $bg="#fffbeb">
          <SnapshotTitle>Overdue (31–60)</SnapshotTitle>
          <SnapshotValue $color="#b45309">{formatCurrency(agingSnapshot.overdue31_60, 'INR')}</SnapshotValue>
        </SnapshotCard>
        <SnapshotCard $bg="#fef2f2">
          <SnapshotTitle>Critical ({'>'}60)</SnapshotTitle>
          <SnapshotValue $color="#991b1b">{formatCurrency(agingSnapshot.critical60Plus, 'INR')}</SnapshotValue>
        </SnapshotCard>
      </SnapshotGrid>
      
      <MetricsBar>
        <span>🔴 {agingSnapshot.blockedPct}% linked to blocked shipments</span>
        <span 
          style={{ position: 'relative', cursor: 'help' }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          🟠 {agingSnapshot.agentLinkedPct}% linked to agents
          {showTooltip && (
            <Tooltip>
              Outstanding where agent is primary account owner or escalation point
            </Tooltip>
          )}
        </span>
        <span>🟢 {agingSnapshot.cleanPendingPct}% clean but pending milestones</span>
      </MetricsBar>

      {/* Section 2: Exposure table */}
      <H3 style={{ marginTop: '24px', marginBottom: '12px' }}>Exposure Table</H3>
      <TableContainer>
        <Table>
          <TableHead>
            <tr>
              <TableHeader>Customer</TableHeader>
              <TableHeader>Order(s)</TableHeader>
              <TableHeader style={{ textAlign: 'right' }}>Outstanding</TableHeader>
              <TableHeader>Aging</TableHeader>
              <TableHeader>Supplier</TableHeader>
              <TableHeader>Compliance Status</TableHeader>
              <TableHeader>Last Action</TableHeader>
              <TableHeader>Next Recommended</TableHeader>
            </tr>
          </TableHead>
          <tbody>
            {rowsWithHighlight.map((row, idx) => (
              <TableRow key={idx} $highlight={!!(highlightOrderId && row.orders.includes(highlightOrderId))}>
                <TableCell><strong>{row.customer}</strong></TableCell>
                <TableCell>
                  {row.orders.map(order => (
                    <Button variant="link" key={order} onClick={() => onOpenComplianceOrder?.(order) || onOpenOrder?.(order)} title="Open compliance workflow" style={{ marginRight: '8px', padding: 0 }}>
                      {order}
                    </Button>
                  ))}
                </TableCell>
                <TableCell style={{ textAlign: 'right' }}>₹{row.outstanding.toLocaleString('en-IN')}</TableCell>
                <TableCell>{row.bucket}</TableCell>
                <TableCell>{row.supplier}</TableCell>
                <TableCell>{row.shipmentStatus}</TableCell>
                <TableCell>{row.lastAction}</TableCell>
                <TableCell>
                  {row.nextAction}
                  {canManage && (
                    <ActionLinks>
                      {row.shipmentStatus !== 'Blocked' && (
                        <ActionLink
                          $variant="danger"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            void handleRowAction('block', row.orders[0]);
                          }}
                        >
                          Block shipment
                        </ActionLink>
                      )}
                      {row.shipmentStatus === 'Blocked' && (
                        <ActionLink
                          $variant="success"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            void handleRowAction('unblock', row.orders[0]);
                          }}
                        >
                          Unblock shipment
                        </ActionLink>
                      )}
                      <ActionLink
                        $variant="primary"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          void handleRowAction('remind', row.orders[0]);
                        }}
                      >
                        Trigger reminder
                      </ActionLink>
                      <ActionLink
                        $variant="purple"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          void handleRowAction('escalate', row.orders[0]);
                        }}
                      >
                        Escalate internally
                      </ActionLink>
                    </ActionLinks>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      {/* Section 3: Blocked Shipments */}
      <H3 style={{ marginTop: '24px', marginBottom: '12px' }}>Blocked Orders</H3>
      <TableContainer>
        <Table>
          <TableHead>
            <tr>
              <TableHeader>Order</TableHeader>
              <TableHeader>Customer</TableHeader>
              <TableHeader>Supplier</TableHeader>
              <TableHeader>Block Reason</TableHeader>
              <TableHeader>Next Step</TableHeader>
            </tr>
          </TableHead>
          <tbody>
            {blockedOrders.map((s, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <Button variant="link" onClick={() => handleShipmentClick(s.orderId)} title="Open compliance workflow" style={{ padding: 0, fontWeight: 600 }}>
                    {s.orderId}
                  </Button>
                </TableCell>
                <TableCell>{s.customer}</TableCell>
                <TableCell>{s.supplier}</TableCell>
                <TableCell>{s.reason}</TableCell>
                <TableCell>{s.unlockCondition}</TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      {!canManage && (
        <Subtitle style={{ marginTop: 8 }}>
          Finance controls the block; Ops executes. Read-only for Admin/Ops.
        </Subtitle>
      )}

      {/* Section 4: Agent risk summary */}
      <H3 style={{ marginTop: '24px', marginBottom: '12px' }}>Supplier Risk Summary</H3>
      <AgentGrid>
        {supplierRiskSummary.map((a, idx) => (
          <AgentCard 
            key={idx} 
            $overduePct={a.overduePct}
            $blockedCount={a.blockedCount}
            onClick={() => handleAgentClick(a.supplier)}
            title="View supplier profile"
          >
            <AgentName>{a.supplier}</AgentName>
            <AgentMetric>Outstanding linked: ₹{a.outstandingLinked.toLocaleString('en-IN')}</AgentMetric>
            <AgentMetric>Overdue %: {a.overduePct}%</AgentMetric>
            <AgentMetric>Blocked shipments: {a.blockedCount}</AgentMetric>
            <AgentMetric>Escalations: {a.escalations}</AgentMetric>
          </AgentCard>
        ))}
      </AgentGrid>
    </ReconciliationContainer>
  );
};

export default ReconciliationOverview;
